import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, Truck, Plus } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

const SCOPES = ["Machining", "Fabrication", "Painting", "Assembly", "Testing", "Reconditioning", "Other"];

export default function ConvertOaToSupplierPoDialog({ open, onOpenChange, oa, onCreated }) {
  const nav = useNavigate();
  const [suppliers, setSuppliers] = useState([]);
  const [creatingNewSupplier, setCreatingNewSupplier] = useState(false);
  const [saving, setSaving] = useState(false);
  const [supplierId, setSupplierId] = useState("");
  const [newSupplier, setNewSupplier] = useState({ name: "", gstin: "", contact_person: "", phone: "", city: "" });
  const [scope, setScope] = useState("Machining");
  const [poDate, setPoDate] = useState(new Date().toISOString().slice(0, 10));
  const [deliveryDate, setDeliveryDate] = useState(oa?.delivery_commitment || "");
  const [paymentTerms, setPaymentTerms] = useState("NET 45");
  const [remarks, setRemarks] = useState("");
  const [selectedLineIds, setSelectedLineIds] = useState(new Set());

  const oaLines = useMemo(() => oa?.line_items || [], [oa]);

  useEffect(() => {
    if (open) {
      api.get("/suppliers").then((r) => setSuppliers(r.data || []));
      setSelectedLineIds(new Set((oa?.line_items || []).map((li) => li.id)));
      setSaving(false); setCreatingNewSupplier(false); setSupplierId("");
      setNewSupplier({ name: "", gstin: "", contact_person: "", phone: "", city: "" });
      setScope("Machining");
      setPoDate(new Date().toISOString().slice(0, 10));
      setDeliveryDate(oa?.delivery_commitment || "");
      setPaymentTerms("NET 45");
      setRemarks("");
    }
  }, [open, oa]);

  function toggleLine(id) {
    setSelectedLineIds((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  }

  const selectedCount = selectedLineIds.size;
  const canSubmit = (creatingNewSupplier ? newSupplier.name.trim().length > 0 : !!supplierId) &&
                    !!poDate && selectedCount > 0;

  async function submit() {
    if (!canSubmit) return;
    setSaving(true);
    try {
      const payload = {
        po_date: poDate, scope, delivery_date: deliveryDate || null,
        payment_terms: paymentTerms || null, remarks: remarks || null,
        line_ids: Array.from(selectedLineIds),
      };
      if (creatingNewSupplier) {
        payload.new_supplier = { ...newSupplier, name: newSupplier.name.trim() };
      } else {
        payload.supplier_id = supplierId;
      }
      const { data } = await api.post(`/order-acceptances/${oa.id}/convert-to-supplier-po`, payload);
      toast.success(`Supplier PO ${data.sp_number} generated`);
      onOpenChange(false);
      if (onCreated) onCreated(data);
      nav(`/supplier-pos/${data.id}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Conversion failed");
    } finally { setSaving(false); }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="convert-oa-supplier-po-dialog" className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Truck className="w-5 h-5 text-red-600" />Convert OA to Supplier PO</DialogTitle>
          <DialogDescription>Generate a sub-contract purchase order using the same line items — supplier chosen below.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Supplier picker */}
          <div className="border border-slate-200 rounded-lg p-3">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs uppercase tracking-wider text-slate-600 font-semibold">Supplier</div>
              <Button size="sm" variant="ghost" onClick={() => setCreatingNewSupplier((v) => !v)}
                      data-testid="toggle-new-supplier">
                <Plus className="w-3 h-3 mr-1" /> {creatingNewSupplier ? "Pick existing" : "New supplier"}
              </Button>
            </div>
            {creatingNewSupplier ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
                <div>
                  <Label className="text-[10px] uppercase">Name *</Label>
                  <Input value={newSupplier.name} data-testid="new-supplier-name"
                         onChange={(e) => setNewSupplier({ ...newSupplier, name: e.target.value })} className="h-8 mt-0.5" />
                </div>
                <div>
                  <Label className="text-[10px] uppercase">GSTIN</Label>
                  <Input value={newSupplier.gstin}
                         onChange={(e) => setNewSupplier({ ...newSupplier, gstin: e.target.value })} className="h-8 mt-0.5" />
                </div>
                <div>
                  <Label className="text-[10px] uppercase">Contact Person</Label>
                  <Input value={newSupplier.contact_person}
                         onChange={(e) => setNewSupplier({ ...newSupplier, contact_person: e.target.value })} className="h-8 mt-0.5" />
                </div>
                <div>
                  <Label className="text-[10px] uppercase">Phone</Label>
                  <Input value={newSupplier.phone}
                         onChange={(e) => setNewSupplier({ ...newSupplier, phone: e.target.value })} className="h-8 mt-0.5" />
                </div>
                <div className="md:col-span-2">
                  <Label className="text-[10px] uppercase">City</Label>
                  <Input value={newSupplier.city}
                         onChange={(e) => setNewSupplier({ ...newSupplier, city: e.target.value })} className="h-8 mt-0.5" />
                </div>
              </div>
            ) : (
              <Select value={supplierId} onValueChange={setSupplierId}>
                <SelectTrigger data-testid="supplier-picker"><SelectValue placeholder="Pick a supplier…" /></SelectTrigger>
                <SelectContent>
                  {suppliers.length === 0 ? (
                    <SelectItem value="__none__" disabled>No suppliers yet — add one</SelectItem>
                  ) : suppliers.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.name}{s.city ? ` · ${s.city}` : ""}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          {/* PO meta */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 text-xs">
            <div>
              <Label className="text-[10px] uppercase">SP Date *</Label>
              <Input type="date" value={poDate} onChange={(e) => setPoDate(e.target.value)} className="mt-0.5 h-8" data-testid="sp-date" />
            </div>
            <div>
              <Label className="text-[10px] uppercase">Scope</Label>
              <Select value={scope} onValueChange={setScope}>
                <SelectTrigger className="mt-0.5 h-8" data-testid="sp-scope"><SelectValue /></SelectTrigger>
                <SelectContent>{SCOPES.map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] uppercase">Delivery Date</Label>
              <Input type="date" value={deliveryDate} onChange={(e) => setDeliveryDate(e.target.value)} className="mt-0.5 h-8" />
            </div>
            <div>
              <Label className="text-[10px] uppercase">Payment Terms</Label>
              <Input value={paymentTerms} onChange={(e) => setPaymentTerms(e.target.value)} className="mt-0.5 h-8" />
            </div>
          </div>

          {/* Line-item picker */}
          <div className="border border-slate-200 rounded-lg overflow-hidden">
            <div className="bg-slate-50 px-3 py-2 text-xs font-semibold text-slate-700 uppercase tracking-wider flex items-center justify-between">
              <span>Lines to include · {selectedCount}/{oaLines.length}</span>
              <button type="button" className="text-[10px] font-normal text-blue-900 hover:underline"
                      onClick={() => setSelectedLineIds(new Set(oaLines.map((li) => li.id)))}>select all</button>
            </div>
            <div className="max-h-64 overflow-auto divide-y divide-slate-100">
              {oaLines.map((li) => (
                <label key={li.id} className="flex items-start gap-3 px-3 py-2 text-xs hover:bg-slate-50 cursor-pointer" data-testid={`sp-line-check-${li.line_no}`}>
                  <Checkbox checked={selectedLineIds.has(li.id)} onCheckedChange={() => toggleLine(li.id)} className="mt-0.5" />
                  <div className="flex-1">
                    <div className="text-slate-900">{li.description}</div>
                    <div className="text-[10px] text-slate-500 font-mono mt-0.5">
                      {li.bore_mm && `Ø${li.bore_mm} `}
                      {li.rod_diameter_mm && `× R${li.rod_diameter_mm} `}
                      {li.stroke_mm && `× S${li.stroke_mm}mm `}
                      · Qty {li.quantity} {li.uom || "Nos"}
                    </div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          <div>
            <Label className="text-[10px] uppercase text-slate-500">Remarks</Label>
            <Textarea rows={2} value={remarks} onChange={(e) => setRemarks(e.target.value)} className="mt-1 text-xs" data-testid="sp-remarks" />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={!canSubmit || saving} data-testid="sp-create-btn"
                  className="bg-red-800 hover:bg-red-900 text-white">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : `Generate Supplier PO (${selectedCount})`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
